/**
 * PATH: packages/ui/src/kanban/components/settings-home/DrawerSettingsPage.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-DRAWER-001
 * PURPOSE: Card-drawer order/hide settings. Reuses CardTabLayoutMenu body.
 * ROLE IN ARCHITECTURE: Settings-home page body over kanban:card-tab-layout.
 * MAIN EXPORTS: DrawerSettingsPage
 * NON-RESPONSIBILITIES: Does not merge into kanban:drawer-sections expand prefs.
 * NOTES FOR FUTURE AI: Hide Team chat must keep Card notes in order.
 */
import { useEffect, useState } from 'react'

import { CardTabLayoutMenu } from '../card-detail/drawer-sections/CardTabLayoutMenu'
import {
  DRAWER_CLASSIC_CHANGE_EVENT,
  readDrawerClassicFlag,
  writeDrawerClassicFlag,
} from '../card-detail/drawer-sections/drawerSectionPrefs'
import {
  moveCardTabBlock,
  patchCardTabBoard,
  DEFAULT_CARD_TAB_LAYOUT,
  resetCardTabLayout,
  resolveCardTabLayout,
  setCardTabBlockHidden,
  type CardTabBlockId,
  type CardTabLayout,
} from '../card-detail/drawer-sections/cardTabLayout'
import { useCardTabLayout } from '../card-detail/drawer-sections/useCardTabLayout'
import type { Board } from '../../types'
import { APPLY_TO_OPTIONS, OptionStrip, SectionShell } from './chrome'
import type { SettingsPage } from './nav'
import { drawerScopeFor } from './nav'
import { trackKanbanSettingsEvent } from '../../../diagnostics/settingsTelemetry'
import { confirmScopedReset, countChangedControls } from './useSettingsUndo'

export function DrawerSettingsPage({
  page,
  onPage,
  board,
  allowBoardScope = true,
}: {
  page: SettingsPage
  onPage: (page: SettingsPage) => void
  board?: Board
  allowBoardScope?: boolean
}) {
  const scope = allowBoardScope ? drawerScopeFor(page) : 'all'
  const boardId = scope === 'board' && board ? board.id : undefined
  const [classic, setClassic] = useState(() => readDrawerClassicFlag(boardId))
  useEffect(() => {
    const sync = () => setClassic(readDrawerClassicFlag(boardId))
    sync()
    window.addEventListener(DRAWER_CLASSIC_CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(DRAWER_CLASSIC_CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [boardId])
  const { layout, replace } = useCardTabLayout(
    scope === 'board' && board ? board.id : undefined,
  )
  const resolved = resolveCardTabLayout(
    layout,
    scope === 'board' && board ? board.id : undefined,
  )

  const persistResolved = (next: CardTabLayout) => {
    if (scope === 'board' && board) {
      replace(
        patchCardTabBoard(layout, board.id, {
          order: next.order,
          hidden: next.hidden,
        }),
      )
    } else {
      replace({
        ...layout,
        order: next.order,
        hidden: next.hidden,
      })
    }
    trackKanbanSettingsEvent('kanban.drawer_save', {
      controlId: page,
      scope: scope === 'board' ? 'board' : 'global',
      status: 'pending',
    })
  }

  const move = (id: CardTabBlockId, direction: 'up' | 'down') => {
    persistResolved(moveCardTabBlock(resolved, id, direction))
  }
  const setHidden = (id: CardTabBlockId, hidden: boolean) => {
    persistResolved(setCardTabBlockHidden(resolved, id, hidden))
  }
  const reset = () => {
    const changed = countChangedControls(
      { order: resolved.order, hidden: resolved.hidden },
      { order: DEFAULT_CARD_TAB_LAYOUT.order, hidden: DEFAULT_CARD_TAB_LAYOUT.hidden },
    )
    if (!confirmScopedReset(changed, 'Reset every drawer control on this scope?')) return
    replace(resetCardTabLayout(layout, scope === 'board' && board ? board.id : undefined))
    trackKanbanSettingsEvent('kanban.drawer_save', {
      controlId: 'drawer.reset',
      scope: scope === 'board' ? 'board' : 'global',
      status: 'success',
    })
  }

  return (
    <div data-testid="settings-drawer-page">
      {allowBoardScope ? (
        <SectionShell
          title="Applies to"
          hint="This list can stay on this board, or become the default on every board."
        >
          <OptionStrip
            label="Where this order is used"
            value={scope}
            options={APPLY_TO_OPTIONS}
            testId="settings-drawer-apply"
            onChange={(id) => onPage(id === 'all' ? 'kanban-drawer' : 'board-drawer')}
          />
        </SectionShell>
      ) : null}
      <SectionShell
        title="Order in the right drawer"
        hint="Hide takes the block off the tab. Mail and notes stay on the card."
      >
        <CardTabLayoutMenu
          variant="inline"
          layout={resolved}
          move={move}
          setHidden={setHidden}
          reset={reset}
        />
      </SectionShell>
      <SectionShell
        title="Classic expand-all"
        hint="Opens every collapsed drawer section. Separate from hide/order."
      >
        <label className="flex items-center justify-between gap-3 py-1 text-xs text-neutral-600 dark:text-zinc-300">
          Expand every section
          <button
            type="button"
            role="switch"
            data-testid="settings-drawer-classic"
            aria-checked={classic}
            onClick={() => {
              const next = !classic
              writeDrawerClassicFlag(next, boardId)
              setClassic(next)
            }}
            className={[
              'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
              classic ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-zinc-700',
            ].join(' ')}
          >
            <span
              className={[
                'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                classic ? 'translate-x-4' : 'translate-x-0.5',
              ].join(' ')}
            />
          </button>
        </label>
      </SectionShell>
    </div>
  )
}
